/**
 * Centralized audio manager for all games.
 *
 * Mobile browsers (especially iOS Safari) require user interaction before playing audio.
 * Call unlock() synchronously from the Start button click handler to enable audio for the session.
 *
 * - Single shared AudioContext (no per-game instances)
 * - Resume on first user gesture
 * - Games use getContext() instead of creating their own
 */

let _audioCtx = null;
let _unlocked = false;

const getAudioContext = () => {
  if (typeof window === 'undefined') return null;
  return window.AudioContext || window.webkitAudioContext;
};

/**
 * Unlock audio for playback. Must be called synchronously from a user gesture (e.g. Start button click).
 * Safe to call multiple times; only runs once.
 */
export const unlock = () => {
  if (_unlocked) return;
  if (typeof window === 'undefined') return;
  _unlocked = true;

  const Ctx = getAudioContext();
  if (!Ctx) return;

  if (!_audioCtx) {
    _audioCtx = new Ctx();
  }
  if (_audioCtx.state === 'suspended') {
    _audioCtx.resume().catch(() => {});
  }
  // Play a silent buffer to fully unlock HTMLAudioElement on iOS
  try {
    const buffer = _audioCtx.createBuffer(1, 1, 22050);
    const src = _audioCtx.createBufferSource();
    src.buffer = buffer;
    src.connect(_audioCtx.destination);
    src.start(0);
  } catch (_) {}
};

/**
 * Get the shared AudioContext. Creates it if needed (after unlock has been called).
 * On first call before unlock, returns null; after unlock, returns the context.
 */
export const getContext = () => {
  if (typeof window === 'undefined') return null;
  if (!_audioCtx) {
    const Ctx = getAudioContext();
    if (Ctx) _audioCtx = new Ctx();
  }
  return _audioCtx;
};

/**
 * Ensure context is resumed. Call before playing if context might be suspended
 * (e.g. tab was backgrounded). Safe to call anytime.
 */
export const resumeIfSuspended = () => {
  if (_audioCtx && _audioCtx.state === 'suspended') {
    _audioCtx.resume().catch(() => {});
  }
};
